import {Component, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { OPTION_MODAL_PASSPORT } from '@app/enums';
import { TypesErrors } from '@app/enums/errors.enum';
import { PassportScanInfoInt } from '@app/interfaces';
import { Passenger } from '@app/models';
import { PassportReaderDeviceService } from '@app/services/devices';
import { MessagesService } from '@app/services/messages.service';
import { PassengerService } from '@app/services/passenger.service';
import { isPassportHardwareErrorPayload, unsubscribe } from '@app/utils';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
/**
  Componente para mostrar un modal de escaneo de pasaporte.
  Este componente permite escanear un pasaporte utilizando un dispositivo de lectura,
  gestionar el estado del escaneo y cerrar el modal con la información obtenida.
**/
@Component({
    selector: 'app-scanpassport-modal',
    templateUrl: './scanpassport-modal.component.html',
    styleUrls: ['./scanpassport-modal.component.scss'],
    standalone: false
})
export class ScanpassportModalComponent implements OnInit, OnDestroy {

/**
    Estado actual del escáner de pasaporte.
    Puede ser: 'wait', 'process', 'success', 'error' o null.
  **/
    status: StatusScanner = 'wait';

    /**
      Pasajero actual que está siendo procesado.
    **/
    currentPassenger: Passenger;
  
    /**
      Opciones de configuración del modal.
      - centered: Centra el modal en la pantalla.
      - size: Tamaño del modal (en este caso, extra grande).
      - backdrop: Evita que el modal se cierre al hacer clic fuera de él.
      - keyboard: Deshabilita el cierre del modal al presionar la tecla ESC.
    **/
    optionsModal: NgbModalOptions = {
      centered: true,
      size: 'xl',
      backdrop: 'static',
      keyboard: false
    };
  
    /**
      Lista de suscripciones para gestionar la desuscripción en el ciclo de vida del componente.
    **/
    subs: Subscription[] = [];
  
    /**
      Identificador del temporizador para gestionar el tiempo de espera.
    **/
    timeoutId = undefined;
  //#endregion Vars

  constructor(
    private _modal: NgbActiveModal,
    private _router: Router,
    private _passengerService: PassengerService,
    private _passportReaderServ: PassportReaderDeviceService,
    private _messages: MessagesService,
  ) { }

  ngOnInit(): void {
    this.initPage();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  //#region Functions internals
  initPage() {
    this.currentPassenger = this._passengerService.getCurrentPassengerIntl();
    this.initDevice();
    this.readPassport();
  }
  /**
    Inicializa el dispositivo de lectura de pasaporte.
    - Habilita el dispositivo.
    - Escucha los eventos del dispositivo para procesar la información del pasaporte.
    @return void - No retorna ningún valor.
  **/
  private scheduleReactivatePassport(): void {
    setTimeout(() => {
      this._passportReaderServ.disable();
      this._passportReaderServ.enable();
    }, 350);
  }

  initDevice() {
    this._passportReaderServ.enable();
    const passportSub = this._passportReaderServ.listen().subscribe({
      next: (response) => {
        if (isPassportHardwareErrorPayload(response)) {
          this._messages.errorMessage({ typesError: TypesErrors.PP_SCAN_FAIL }, () =>
            this.scheduleReactivatePassport()
          );
          return;
        }
        if (response?.event?.key == 'passportReadInternal') {
          this.successReadPassport(response.passportInfo);
        }
      },
      error: (err) => {
        this._messages.errorMessage(
          { title: 'Error', text: err.toString(), typesError: TypesErrors.PP_SCAN_FAIL },
          () => this.scheduleReactivatePassport()
        );
      },
    });
    this.subs.push(passportSub);
  }

    /**
    Procesa la información del pasaporte escaneado con éxito.
    - Deshabilita el dispositivo de lectura.
    - Cierra el modal con la información del pasaporte.
    @param passportInfo Información del pasaporte escaneado.
    @return void - No retorna ningún valor.
  **/

  successReadPassport(passportInfo: PassportScanInfoInt) {
    const firstName = passportInfo.firstName;
    const lastName = passportInfo.lastName
    if (firstName || lastName) {
      this._passportReaderServ.disable();
      this.closeModal({option: OPTION_MODAL_PASSPORT.READ_SUCCESS,infoPassport: passportInfo});
    } else {
      this.status = 'error';
    }
  }

  readPassport(){
    this.status = 'wait';
  }
  /**
    Cierra el modal con la respuesta proporcionada.
    - Limpia el temporizador si está activo.
    - Cierra el modal con la opción y la información del pasaporte.
    @param response Respuesta que se enviará al cerrar el modal.
    @return void - No retorna ningún valor.
  **/
  closeModal(response = {option: OPTION_MODAL_PASSPORT.WRITE_MANUAL_DATA,infoPassport: undefined}){
    if(this.timeoutId){
      clearTimeout(this.timeoutId)
    }
    this._modal.close(response)
  }

  //#endregion Functions internals

  //#region Events
  onToCapturePassport(): void {
    this.closeModal()
  }

  onReadPassport(){
    this.readPassport();
  }

  onBack() {
    this._router.navigate(['/flight-info'])
  }
  //#endregion Events

}

type StatusScanner = "wait" | "process" | 'success' | 'error' | null
